/**
 * What kind of microphone a meeting is captured on, as the gateway's
 * `micKind` takes it. A headset hears only its wearer, so the planner reads
 * every voice on it as the user. Anything else may hear a room.
 */
export type MicKind = 'headset' | 'other'

/** Room hardware that can share a headset maker's name: "Jabra Speak 510", "Poly Sync Room". */
const ROOM_DEVICE = /\bspeak\b|conference|\bpuck\b|speakerphone|\bmeeting\b|\broom\b/i

const HEADSET =
  /airpods|\bbuds\d*\b|\bbeats\b|powerbeats|headphones?|headset|earbuds?|\bbose\s+(qc|nc)|quietcomfort|\bsony\s+w[hf]-?|\bw[hf]-\d|jabra\s+(evolve|elite)/i

/**
 * Windows names a Bluetooth hands-free capture endpoint by its role, "Headset
 * (Poly Sync 20 Hands-Free AG Audio)", for pucks and earbuds alike: only the
 * device in brackets says what the hardware is.
 */
const WINDOWS_ROLE_ENDPOINT = /^(?:headset|headphones?)(?:\s+microphone)?\s*\((.+)\)\s*$/i

/**
 * The kind of mic a device label names. Only a recognised headset counts:
 * a built-in mic, a USB or Bluetooth conference puck, or a name we don't
 * know reads as `other`, where the planner keeps treating the mic as a room.
 */
export function micKindForLabel(label: string | undefined): MicKind {
  const device = label?.match(WINDOWS_ROLE_ENDPOINT)?.[1] ?? label
  if (!device || ROOM_DEVICE.test(device)) return 'other'
  return HEADSET.test(device) ? 'headset' : 'other'
}
